import { hubLaborExpectedPageFromPathname, isWorkingSubpageKey } from "@/lib/labor/laborPageKey";
import { laborPageKeysEquivalent } from "@/pages/clampqc/clampQcLaborClockState";

export type LaborHandoffDecision = "noop" | "handoff" | "clock_out";

/**
 * Decision for SPA navigation in the tab that owns labor presence:
 * - `handoff`   — same tab moving from one working page to another working page.
 * - `clock_out` — same tab moving from the working page to a non-working hub page.
 *                 The owning tab silently ends the session as part of leaving the page.
 *                 Cross-tab / cross-computer lockdown is still handled by `LaborLockdownGate`.
 * - `noop`      — every other case (path unchanged, presence in a different tab, presence is
 *                 external, the previous URL is not the page that owns presence, or there is no
 *                 presence at all).
 */
export function decideLaborHandoff(input: {
  openSessionPage: string | null;
  openSessionTabId: string | null;
  currentTabId: string;
  previousPathname: string;
  previousSearch?: string;
  nextPathname: string;
  nextSearch?: string;
}): LaborHandoffDecision {
  const {
    openSessionPage,
    openSessionTabId,
    currentTabId,
    previousPathname,
    previousSearch = "",
    nextPathname,
    nextSearch = "",
  } = input;
  if (previousPathname === nextPathname && previousSearch === nextSearch) {
    return "noop";
  }
  if (openSessionPage == null || openSessionPage.trim() === "") {
    return "noop";
  }
  const prevKey = hubLaborExpectedPageFromPathname(previousPathname, previousSearch);
  const nextKey = hubLaborExpectedPageFromPathname(nextPathname, nextSearch);
  if (laborPageKeysEquivalent(prevKey, nextKey)) {
    return "noop";
  }
  if (openSessionTabId !== null && openSessionTabId !== currentTabId) {
    return "noop";
  }
  if (openSessionTabId === null) {
    if (!laborPageKeysEquivalent(openSessionPage.trim(), prevKey.trim())) {
      return "noop";
    }
    if (isWorkingSubpageKey(nextKey)) {
      return "noop";
    }
    return "clock_out";
  }
  if (!laborPageKeysEquivalent(openSessionPage.trim(), prevKey.trim())) {
    // Owning tab left a working surface for a non-working route while the open session row still
    // points at a different job key (for example error-bump carton 6 → queue while session is on carton 7).
    if (
      openSessionTabId === currentTabId &&
      isWorkingSubpageKey(prevKey) &&
      !isWorkingSubpageKey(nextKey)
    ) {
      return "clock_out";
    }
    if (
      openSessionTabId === currentTabId &&
      isWorkingSubpageKey(prevKey) &&
      isWorkingSubpageKey(nextKey)
    ) {
      return "handoff";
    }
    return "noop";
  }
  if (isWorkingSubpageKey(nextKey)) {
    return "handoff";
  }
  return "clock_out";
}

/**
 * Whether SPA navigation should pause until labor handoff / clock-out finishes.
 * Blocks when the owning tab leaves a working surface; the async handler refetches open-session
 * when local React Query state is stale.
 */
export function shouldBlockLaborNavigation(input: {
  previousPathname: string;
  previousSearch?: string;
  nextPathname: string;
  nextSearch?: string;
  openSessionPage: string | null;
  openSessionTabId: string | null;
  currentTabId: string;
}): boolean {
  const {
    previousPathname,
    previousSearch = "",
    nextPathname,
    nextSearch = "",
    openSessionPage,
    openSessionTabId,
    currentTabId,
  } = input;
  if (previousPathname === nextPathname && previousSearch === nextSearch) {
    return false;
  }
  const prevKey = hubLaborExpectedPageFromPathname(previousPathname, previousSearch);
  if (!isWorkingSubpageKey(prevKey)) {
    return false;
  }
  const nextKey = hubLaborExpectedPageFromPathname(nextPathname, nextSearch);
  if (laborPageKeysEquivalent(prevKey, nextKey)) {
    return false;
  }
  if (openSessionPage != null && openSessionTabId != null) {
    if (openSessionTabId !== currentTabId) {
      return false;
    }
    return (
      decideLaborHandoff({
        openSessionPage,
        openSessionTabId,
        currentTabId,
        previousPathname,
        previousSearch,
        nextPathname,
        nextSearch,
      }) !== "noop"
    );
  }
  // React Query open-session can lag behind a page-local clock-in; refetch before committing.
  return true;
}
